import { InvalidCatalogDefinitionError } from "./errors.model";
import type { CanonicalLineage } from "./lib/canonical";
import { assignMemberIds, highWaterMark, type Member, type MemberId, memberIdSequence } from "./member-identity";
import {
  assertUnpinnedReference,
  CatalogDefinition,
  type CatalogSpine,
  type ReferenceAvailabilityFacts,
} from "./spine.model";

// ============================================================
// ExamDefinition — sections of unpinned registry finding lineages
//
// The registry owns clinical content and laterality; the exam arranges it.
// Placements carry only a finding lineage and an optional prerequisite.
// Diagnostic links belong to recorded findings at clinician discretion.
// ============================================================

const SECTION_PREFIX = "sec";

/**
 * One finding, as placed in one exam. The whole of what the exam gets to say
 * about it.
 */
export interface ExamFindingInput {
  /** The `FindingDefinition` lineage this placeholder resolves, by bare canonical url. */
  finding: string;
  /**
   * Another placeholder, by canonical url. If that finding was not performed,
   * this one INHERITS ITS STATE — DidNotPerform or UnableToPerform, whichever
   * the prerequisite ended in.
   *
   * ONE prerequisite, not a list. PERFORMANCE-BASED ONLY, never value-based: no
   * "if the answer was X, show Y" — value-driven branching is what makes a
   * questionnaire an interview, and an exam is a battery a clinician works
   * through.
   */
  prerequisite?: CanonicalLineage;
}

/** A placeholder the aggregate has seen. Identical to the input: its identity is its canonical url. */
export type ExamDefinitionFinding = ExamFindingInput;

export interface ExamSectionInput extends Member {
  label: string;
  findings: readonly ExamFindingInput[];
}

export interface ExamSection extends ExamSectionInput {
  readonly id: MemberId;
  readonly findings: readonly ExamDefinitionFinding[];
}

/**
 * The high-water mark of the section identity sequence. AGGREGATE STATE THAT
 * MUST BE PERSISTED: seeding a sequence from the live members reuses the
 * identity of a removed member the moment the removed one was the highest,
 * which is the exact failure assigned identities exist to prevent.
 */
export interface ExamMemberIdMarks {
  sections: number;
}

export interface ExamDefinitionFacts {
  spine: CatalogSpine;
  examNoteLabel?: string;
  sections: readonly ExamSection[];
  marks: ExamMemberIdMarks;
}

/**
 * A stored revision as persistence actually holds it: a row written before the
 * mark was persisted carries none. The constructor already treats an absent
 * mark as a sequence seeded from the members present, so nothing else changes.
 */
export interface StoredExamDefinitionProps extends Omit<ExamDefinitionFacts, "marks"> {
  marks?: ExamMemberIdMarks;
}

interface ExamDefinitionInput {
  spine: CatalogSpine;
  examNoteLabel?: string;
  sections: readonly ExamSectionInput[];
  /** Supplied when rehydrating. Absent on a brand new lineage, whose sequence starts at zero. */
  marks?: ExamMemberIdMarks;
}

export class ExamDefinition extends CatalogDefinition<ExamDefinition> {
  private examSections: readonly ExamSection[];
  private noteLabel?: string;
  private marks: ExamMemberIdMarks;

  constructor(input: ExamDefinitionInput) {
    super(input.spine);
    this.noteLabel = assertOptionalLabel(input.examNoteLabel, "exam note label", input.spine.url);
    // Seeded from the payload as well as the stored mark: a caller handing in
    // identified sections without their mark would otherwise start minting at
    // one and collide on the first new section. `highWaterMark` cannot see
    // removed members, which is why the stored mark still wins when it is ahead.
    this.marks = { sections: Math.max(input.marks?.sections ?? 0, highWaterMark(input.sections)) };
    // Construction has no prior state to contradict, so whatever identities the
    // payload carries are the ones this lineage knows.
    const adopted = this.adopt(input.sections, idsOf(input.sections));
    this.examSections = adopted.sections;
    this.marks = adopted.marks;
  }

  get sections(): readonly ExamSection[] {
    return structuredClone(this.examSections) as readonly ExamSection[];
  }

  get examNoteLabel(): string | undefined {
    return this.noteLabel;
  }

  get memberIdMarks(): ExamMemberIdMarks {
    return { ...this.marks };
  }

  /** Every placeholder, in section order. Sections are presentational, so this is the meaningful list. */
  get findings(): readonly ExamDefinitionFinding[] {
    return findingsOf(this.sections);
  }

  /**
   * The finding lineages this exam references. What the availability check and
   * the booking-time resolution both operate on. No dedupe is needed — a
   * finding appears at most once — but the set is taken anyway so the two
   * getters read the same.
   */
  get declaredFindings(): readonly string[] {
    return [...new Set(findingsOf(this.examSections).map((finding) => finding.finding))];
  }

  snapshot(): ExamDefinitionFacts {
    return this.facts;
  }

  get facts(): ExamDefinitionFacts {
    return {
      spine: this.spineFacts,
      examNoteLabel: this.noteLabel,
      sections: this.sections,
      marks: this.memberIdMarks,
    };
  }

  protected copyForRevision(spine: CatalogSpine): ExamDefinition {
    return new ExamDefinition({
      spine,
      examNoteLabel: this.noteLabel,
      sections: this.sections,
      marks: { ...this.marks },
    });
  }

  protected substantiveFacts(): unknown {
    return { examNoteLabel: this.noteLabel, sections: this.examSections };
  }

  setExamNoteLabel(examNoteLabel: string | undefined): void {
    this.noteLabel = assertOptionalLabel(examNoteLabel, "exam note label", this.url);
  }

  /**
   * Replace the whole arrangement.
   *
   * ONE SETTER, NOT `addFinding` / `removeFinding` / `moveFinding`. The one
   * cross-member rule — dropping a placeholder orphans anything that names it
   * as a prerequisite — is enforced ON THE RESULT: whatever sections it is
   * handed, a prerequisite naming a findingUrl that is not there is rejected.
   * Structural commands would not make that rule stronger, they would give it
   * several entry points. It is also the shape the builder already produces:
   * the form holds the whole tree until Save.
   */
  setSections(sections: readonly ExamSectionInput[], facts: ReferenceAvailabilityFacts): void {
    this.assertReferencesActive(facts);
    const adopted = this.adopt(sections, new Set(this.examSections.map((section) => section.id)));
    this.examSections = adopted.sections;
    this.marks = adopted.marks;
  }

  private adopt(
    incoming: readonly ExamSectionInput[],
    known: ReadonlySet<MemberId>,
  ): { sections: readonly ExamSection[]; marks: ExamMemberIdMarks } {
    const sequence = memberIdSequence(SECTION_PREFIX, this.marks.sections);
    const sections = assignMemberIds(incoming, known, sequence, "section").map(
      (section): ExamSection => ({
        ...section,
        id: identified(section.id, "section"),
        findings: section.findings.map((finding) => normalize(finding)),
      }),
    );

    this.validate(sections);
    return { sections, marks: { sections: sequence.high } };
  }

  private validate(sections: readonly ExamSection[]): void {
    if (sections.length === 0) {
      throw new InvalidCatalogDefinitionError("An exam must have at least one section", { url: this.url });
    }
    for (const section of sections) {
      if (section.label.trim() === "") {
        throw new InvalidCatalogDefinitionError(`Exam section '${section.id}' must have a label`, {
          url: this.url,
          memberId: section.id,
          collection: "section",
        });
      }
      if (section.findings.length === 0) {
        throw new InvalidCatalogDefinitionError(`Exam section '${section.id}' must have at least one finding`, {
          url: this.url,
          memberId: section.id,
          collection: "section",
        });
      }
    }

    const seen = new Set<string>();
    for (const finding of findingsOf(sections)) {
      const where = { url: this.url, findingUrl: finding.finding, collection: "finding" };
      // A catalog reference identifies a LINEAGE and resolves to its current
      // revision. Pins exist only on the work produced, never here — an exam
      // pinned to revision 4 of a finding would keep asking a question the
      // catalog has moved past.
      assertUnpinnedReference(finding.finding);
      if (seen.has(finding.finding)) {
        throw new InvalidCatalogDefinitionError(`Finding '${finding.finding}' appears twice in this exam`, where);
      }
      seen.add(finding.finding);
    }

    this.validatePrerequisites(sections);
  }

  /**
   * Every prerequisite names a placeholder that exists, and the graph has no
   * cycle. Cycle detection is cheap and a cycle would hang the charting UI,
   * which walks the graph to decide what to skip.
   */
  private validatePrerequisites(sections: readonly ExamSection[]): void {
    const findings = new Map(findingsOf(sections).map((finding) => [finding.finding, finding] as const));

    for (const finding of findings.values()) {
      const prerequisite = finding.prerequisite;
      if (prerequisite === undefined) continue;
      assertUnpinnedReference(prerequisite);
      const where = { url: this.url, findingUrl: finding.finding, collection: "finding" };
      if (prerequisite === finding.finding) {
        throw new InvalidCatalogDefinitionError(`Finding '${finding.finding}' cannot depend on itself`, where);
      }
      if (!findings.has(prerequisite)) {
        throw new InvalidCatalogDefinitionError(
          `Finding '${finding.finding}' names a prerequisite '${prerequisite}' that is not in this exam`,
          where,
        );
      }
    }

    const visiting = new Set<string>();
    const visited = new Set<string>();
    const visit = (findingUrl: string): void => {
      if (visiting.has(findingUrl)) {
        throw new InvalidCatalogDefinitionError("Exam finding prerequisites contain a cycle", {
          url: this.url,
          findingUrl,
          collection: "finding",
        });
      }
      if (visited.has(findingUrl)) return;
      visiting.add(findingUrl);
      const prerequisite = findings.get(findingUrl)?.prerequisite;
      if (prerequisite) visit(prerequisite);
      visiting.delete(findingUrl);
      visited.add(findingUrl);
    };
    for (const findingUrl of findings.keys()) visit(findingUrl);
  }
}

/** Copy the placeholder references and arrangement. */
function normalize(finding: ExamFindingInput): ExamDefinitionFinding {
  return {
    finding: finding.finding,
    ...(finding.prerequisite === undefined ? {} : { prerequisite: finding.prerequisite }),
  };
}

function findingsOf<F>(sections: readonly { findings: readonly F[] }[]): F[] {
  return sections.flatMap((section) => [...section.findings]);
}

function idsOf(members: readonly Member[]): Set<MemberId> {
  const ids = new Set<MemberId>();
  for (const member of members) {
    if (member.id !== undefined) ids.add(member.id);
  }
  return ids;
}

/** `assignMemberIds` always returns an identified member; this is where that guarantee is stated. */
function identified(id: MemberId | undefined, collection: string): MemberId {
  if (id === undefined) {
    throw new InvalidCatalogDefinitionError(`A ${collection} member was not assigned an identity`, { collection });
  }
  return id;
}

/** A label that is present must say something — a blank prompt reaches a clinician as an unlabelled box. */
function assertOptionalLabel(label: string | undefined, what: string, url: string): string | undefined {
  if (label !== undefined && label.trim() === "") {
    throw new InvalidCatalogDefinitionError(`An exam's ${what} must not be blank`, { url });
  }
  return label;
}
